<template>
  <v-container fluid class="mt-3 docForm">
    <v-row>
      <v-col cols="12">
        <v-toolbar color="secondary" flat dense>
          <v-toolbar-title>Record Production</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="primary" small text to="/production-records">
            <v-icon left dark>mdi-keyboard-backspace</v-icon>Back
          </v-btn>
        </v-toolbar>
      </v-col>
    </v-row>

    <v-card class="mt-2">
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            name="productionTitle"
            outlined
            dense
            label="Title / Description"
            v-model="title"
            type="text"
            autocomplete="off"
            class="px-2"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3" offset-md="3">
          <div class="pr-2">
            <DatePicker class="px-2" @date="setDate" :myDate="date" :title="`Date`" ref="dateRef"></DatePicker>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar color="secondary" flat dense>
            <v-toolbar-title>Raw Materials Used</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <span
                  style="font-weight: bold; font-size: 16px"
                >Total Raw Materials Cost: {{ totalRawMaterials }}</span>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <!-- start of table -->
                <v-data-table
                  :items-per-page="pageItems"
                  :headers="headers"
                  :items="rawMaterials"
                  :mobile-breakpoint="zeroValue"
                  hide-default-footer
                  disable-sort
                >
                  <template v-slot:item.Name="{ item }">
                    <!-- autocomplete -->
                    <v-text-field
                      style="width: 250px"
                      class="orderInput"
                      single-line
                      dense
                      readonly
                      v-model="item.Name"
                    ></v-text-field>
                  </template>

                  <template v-slot:item.Quantity="{ item }">
                    <v-text-field
                      style="width: 100px"
                      autocomplete="off"
                      type="number"
                      class="orderInput"
                      @input="qtyChangedRawMaterial(item)"
                      single-line
                      outlined
                      dense
                      v-model="item.Quantity"
                    ></v-text-field>
                  </template>

                  <template v-slot:item.UomEntry="{ item }">
                    <v-text-field
                      style="width: 100px"
                      filled
                      outlined
                      readonly
                      dense
                      value="Unit(s)"
                      v-if="item.ManualStatus == 'Y'"
                    ></v-text-field>

                    <v-autocomplete
                      v-else
                      style="width: 100px"
                      dense
                      autocomplete="off"
                      outlined
                      single-line
                      v-model="item.UomEntry"
                      :items="item.ugp1"
                      label="UOM"
                      item-text="UomName"
                      item-value="UomId"
                      @change="rawMaterialUomChange(item, item.ugp1)"
                    ></v-autocomplete>
                  </template>

                  <!-- action template -->
                  <template v-slot:item.action="{ item }">
                    <v-btn @click="deleteRawMaterialRow(item)" color="red" icon>
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <!-- end -->
                </v-data-table>
                <!-- end -->

                <v-autocomplete
                  style="width: 500px"
                  autocomplete="off"
                  v-model="selectedRawMaterial"
                  :isloading="isLoading"
                  :items="rawMaterialProducts"
                  :search-input.sync="searchRawMaterials"
                  @change="setRawMaterialItem(selectedRawMaterial)"
                  item-text="Name"
                  item-value="id"
                  cache-items
                  class="mt-2"
                  flat
                  hide-no-data
                  hide-details
                  label="Search Raw Materials"
                  outlined
                  dense
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card>
          <v-toolbar color="secondary" flat dense>
            <v-toolbar-title>Products Produced</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
          <v-card-text>
            <v-row>
              <v-col cols="12">
                <span
                  style="font-weight: bold; font-size: 16px"
                >Total Production Value: {{ totalProducedGoods }}</span>
              </v-col>

              <v-col cols="12" sm="12" md="12">
                <!-- start of table -->
                <v-data-table
                  :items-per-page="pageItems"
                  :headers="producedGoodsHeaders"
                  :items="producedGoods"
                  :mobile-breakpoint="zeroValue"
                  hide-default-footer
                  disable-sort
                >
                  <template v-slot:item.Name="{ item }">
                    <!-- autocomplete -->
                    <v-text-field
                      style="width: 250px"
                      class="orderInput"
                      single-line
                      dense
                      readonly
                      v-model="item.Name"
                    ></v-text-field>
                  </template>

                  <template v-slot:item.Quantity="{ item }">
                    <v-text-field
                      style="width: 100px"
                      autocomplete="off"
                      type="number"
                      class="orderInput"
                      @input="qtyChangedProduction(item)"
                      single-line
                      outlined
                      dense
                      v-model="item.Quantity"
                    ></v-text-field>
                  </template>

                  <template v-slot:item.UomEntry="{ item }">
                    <v-text-field
                      style="width: 100px"
                      filled
                      outlined
                      readonly
                      dense
                      value="Unit(s)"
                      v-if="item.ManualStatus == 'Y'"
                    ></v-text-field>

                    <v-autocomplete
                      v-else
                      style="width: 100px"
                      dense
                      autocomplete="off"
                      outlined
                      single-line
                      v-model="item.UomEntry"
                      :items="item.ugp1"
                      label="UOM"
                      item-text="UomName"
                      item-value="UomId"
                      @change="productionUomChange(item, item.ugp1)"
                    ></v-autocomplete>
                  </template>

                  <!-- action template -->
                  <template v-slot:item.action="{ item }">
                    <v-btn @click="deleteProductionRow(item)" color="red" icon>
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </template>
                  <!-- end -->
                </v-data-table>
                <!-- end -->

                <v-autocomplete
                  style="width: 500px"
                  autocomplete="off"
                  v-model="selectedProduct"
                  :isloading="isLoading"
                  :items="products"
                  :search-input.sync="searchProducts"
                  @change="setProductionItem(selectedProduct)"
                  item-text="Name"
                  item-value="id"
                  cache-items
                  class="mt-2"
                  flat
                  hide-no-data
                  hide-details
                  label="Search Product"
                  outlined
                  dense
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <v-col cols="12">
        <v-btn color="primary" class="float-right" :loading="loader" :disabled="loader" @click="save">
          <v-icon left>mdi-content-save</v-icon>Save Production
        </v-btn>
      </v-col>
    </v-row>

    <snackbar ref="snackbar"></snackbar>

    <v-divider></v-divider>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    zeroValue: 0,
    title: "",
    search: "",
    date: new Date().toISOString().substr(0, 10),
    selectedRawMaterial: "",
    selectedProduct: "",
    isLoading: false,
    products: [],
    rawMaterialProducts: [],
    rawMaterials: [],
    producedGoods: [],
    searchProducts: null,
    searchRawMaterials: null,
    select: null,
    pageItems: 100,
    headers: [
      { text: "Name", value: "Name" },
      { text: "Quantity", value: "Quantity" },
      { text: "Unit", value: "UomEntry" },
      { text: "Cost", value: "Price" },
      { text: "Total", value: "DisplayTotal" },
      { text: "Action", value: "action", sortable: false }
    ],
    producedGoodsHeaders: [
      { text: "Name", value: "Name" },
      { text: "Quantity", value: "Quantity" },
      { text: "Unit", value: "UomEntry" },
      { text: "Cost", value: "Price" },
      { text: "Total", value: "DisplayTotal" },
      { text: "Action", value: "action", sortable: false }
    ],
    customers: [],
    customersDialog: false,
    excelFile: null,
    loader: false
  }),
  computed: {
    activeStore() {
      return localStorage.getItem("store_id");
    },
    totalRawMaterials() {
      let total = 0;
      if (
        typeof this.rawMaterials != "undefined" &&
        this.rawMaterials.length > 0
      ) {
        return this.rawMaterials
          .map(item => {
            const amount = item.LineTotal;
            total = +amount;
            return total;
          })
          .reduce((val, a) => {
            return val + a;
          });
      } else {
        return total;
      }
    },
    totalProducedGoods() {
      let total = 0;
      if (
        typeof this.producedGoods != "undefined" &&
        this.producedGoods.length > 0
      ) {
        return this.producedGoods
          .map(item => {
            const amount = item.LineTotal;
            total = +amount;
            return total;
          })
          .reduce((val, a) => {
            return val + a;
          });
      } else {
        return total;
      }
    }
  },
  watch: {
    searchRawMaterials(val) {
      if (val && val !== this.select && this.queryRawMaterials(val)) {
        this.queryRawMaterials(val);
      }
    },
    searchProducts(val) {
      if (val && val !== this.select && this.querySelections(val)) {
        this.querySelections(val);
      }
    }
  },
  methods: {
    setDate(date) {
      this.date = date;
    },
    save() {
      if (this.title == "") {
        this.$refs.snackbar.show("Please enter title", "red");
        return;
      }

      if (this.rawMaterials.length == 0) {
        this.$refs.snackbar.show("Please add raw materials", "red");
        return;
      }

      if (this.producedGoods.length == 0) {
        this.$refs.snackbar.show("Please add produced goods", "red");
        return;
      }

      var rawMaterialsData = [];
      for (const record of this.rawMaterials) {
        var details = {
          ProductId: record.o_i_t_m_s_id,
          UomEntry: record.UomEntry,
          Quantity: parseFloat(record.Quantity),
          Price: parseFloat(record.Price),
          LineTotal: parseFloat(record.LineTotal)
        };

        rawMaterialsData.push(details);
      }

      var producedGoodsData = [];
      for (const row of this.producedGoods) {
        var productionDetails = {
          ProductId: row.o_i_t_m_s_id,
          UomEntry: row.UomEntry,
          Quantity: parseFloat(row.Quantity),
          Price: parseFloat(row.Price),
          LineTotal: parseFloat(row.LineTotal)
        };

        producedGoodsData.push(productionDetails);
      }

      var data = {
        title: this.title,
        date: this.date,
        rawMaterials: rawMaterialsData,
        producedGoods: producedGoodsData
      };

      const url = "/create-manual-production";
      const self = this;
      this.loader = true;
      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            self.$router.push("/production-records");
          } else {
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.$store.commit("btnLoader", false);
          this.$refs.snackbar.show(err, "red");
        }).finally(() => {
          self.loader = false;
        });
    },
    deleteRawMaterialRow(item) {
      const selectedRow = this.rawMaterials.find(record => {
        return record.LineNum === item.LineNum;
      });
      const pos = this.rawMaterials.indexOf(selectedRow);
      if (pos === -1) {
        return;
      }
      this.rawMaterials.splice(pos, 1);
    },
    deleteProductionRow(item) {
      const selectedRow = this.producedGoods.find(record => {
        return record.LineNum === item.LineNum;
      });
      const pos = this.producedGoods.indexOf(selectedRow);
      if (pos === -1) {
        return;
      }
      this.producedGoods.splice(pos, 1);
    },
    rawMaterialUomChange(item, ugp1) {
      const currentItem = this.rawMaterials.find(record => {
        return record.LineNum === item.LineNum;
      });
      const pos = this.rawMaterials.indexOf(currentItem);
      if (pos === -1) {
        return;
      }

      const ugp1Record = ugp1.find(record => {
        return record.UomEntry === item.UomEntry;
      });

      currentItem.Price = ugp1Record.BuyingPrice;
      this.rawMaterials[pos] = currentItem;
      this.qtyChangedRawMaterial(currentItem);
    },
    productionUomChange(item, ugp1) {
      const currentItem = this.producedGoods.find(record => {
        return record.LineNum === item.LineNum;
      });
      const pos = this.producedGoods.indexOf(currentItem);
      if (pos === -1) {
        return;
      }

      const ugp1Record = ugp1.find(record => {
        return record.UomEntry === item.UomEntry;
      });

      currentItem.Price = ugp1Record.BuyingPrice;
      this.producedGoods[pos] = currentItem;
      this.qtyChangedProduction(currentItem);
    },
    qtyChangedRawMaterial(data) {
      let Price;
      let Quantity;
      if (data.Price) {
        Price = parseFloat(data.Price);
      } else {
        Price = 0;
      }
      if (data.Quantity) {
        Quantity = parseFloat(data.Quantity);
      } else {
        Quantity = 0;
      }

      const TotalCost = Price * Quantity;
      const LineTotal = Math.round(TotalCost);
      const DisplayTotal = LineTotal.toLocaleString();
      // find item
      const currentItem = this.rawMaterials.find(record => {
        return record.LineNum === data.LineNum;
      });
      const pos = this.rawMaterials.indexOf(currentItem);
      if (pos === -1) {
        return;
      }
      // end of find

      currentItem.LineTotal = LineTotal;
      currentItem.DisplayTotal = DisplayTotal;
      this.rawMaterials[pos] = currentItem;
    },
    qtyChangedProduction(data) {
      let Price;
      let Quantity;
      if (data.Price) {
        Price = parseFloat(data.Price);
      } else {
        Price = 0;
      }
      if (data.Quantity) {
        Quantity = parseFloat(data.Quantity);
      } else {
        Quantity = 0;
      }

      const TotalCost = Price * Quantity;
      const LineTotal = Math.round(TotalCost);
      const DisplayTotal = LineTotal.toLocaleString();
      // find item
      const currentItem = this.producedGoods.find(record => {
        return record.LineNum === data.LineNum;
      });
      const pos = this.producedGoods.indexOf(currentItem);
      if (pos === -1) {
        return;
      }
      // end of find

      currentItem.LineTotal = LineTotal;
      currentItem.DisplayTotal = DisplayTotal;
      this.producedGoods[pos] = currentItem;
    },
    setRawMaterialItem(id) {
      const store_id = this.activeStore;
      const self = this;
      var customerId = 0;

      var data = {
        CustomerId: parseInt(customerId),
        ProductId: parseInt(id)
      };

      var url = `/product-prices`;
      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          console.log(res, " raw material res ");
          if (res.ResultCode == 1200) {
            const product = res.ResponseData;

            // current item definition
            let currentItem = {};
            currentItem.Quantity = 1;
            currentItem.ManualStatus = product.HasUom == 0 ? "Y" : "N";
            currentItem.UomEntry = product.IUoMEntry;
            currentItem.o_i_t_m_s_id = id;
            currentItem.Name = product.Name;
            currentItem.store_id = store_id;
            const LineNum = new Date().getTime();
            currentItem.LineNum = LineNum;
            currentItem.DiscSum = null;
            // end

            let initArray = [];
            if (product.HasUom === 0) {
              // check if manual yes and assign
              var sellingPrice = product.sellingPrice;
              var buyingPrice = product.buyingPrice;
              currentItem.BuyingPrice = buyingPrice;
              currentItem.Price = sellingPrice;
              currentItem.currentPrice = sellingPrice;
              currentItem.UomEntry = 0;
              const total = 1 * parseFloat(sellingPrice);
              currentItem.LineTotal = total;
              currentItem.DisplayTotal = total.toLocaleString();

              initArray.push(currentItem);
              this.rawMaterials = [...this.rawMaterials, ...initArray];
              this.selectedRawMaterial = "";
              // end of manual yes
            }

            // start of manual No status
            if (product.HasUom === 1) {
              let ugp1Data = product.ugp1;
              const ugp1Record = ugp1Data.find(record => {
                return record.UomEntry === product.IUoMEntry;
              });
              currentItem.ugp1 = product.ugp1;
              currentItem.Price = ugp1Record.SellingPrice;
              currentItem.BuyingPrice = ugp1Record.BuyingPrice;
              currentItem.currentPrice = ugp1Record.SellingPrice;
              const total = 1 * ugp1Record.SellingPrice;
              currentItem.LineTotal = total;
              currentItem.DisplayTotal = total.toLocaleString();

              // push to array
              initArray.push(currentItem);
              this.rawMaterials = [...this.rawMaterials, ...initArray];
              this.selectedRawMaterial = "";
              self.rawMaterialProducts = [];
              // end of push
            }
          } else {
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    setProductionItem(id) {
      const store_id = this.activeStore;
      const self = this;
      var customerId = 0;

      var data = {
        CustomerId: parseInt(customerId),
        ProductId: parseInt(id)
      };

      var url = `/product-prices`;
      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            const product = res.ResponseData;

            // current item definition
            let currentItem = {};
            currentItem.Quantity = 1;
            currentItem.ManualStatus = product.HasUom == 0 ? "Y" : "N";
            currentItem.UomEntry = product.IUoMEntry;
            currentItem.o_i_t_m_s_id = id;
            currentItem.Name = product.Name;
            currentItem.store_id = store_id;
            const LineNum = new Date().getTime();
            currentItem.LineNum = LineNum;
            currentItem.DiscSum = null;
            // end

            let initArray = [];
            if (product.HasUom === 0) {
              // check if manual yes and assign
              var sellingPrice = product.sellingPrice;
              var buyingPrice = product.buyingPrice;
              currentItem.BuyingPrice = buyingPrice;
              currentItem.Price = sellingPrice;
              currentItem.UomEntry = 0;
              currentItem.currentPrice = sellingPrice;
              const total = 1 * parseFloat(sellingPrice);
              currentItem.LineTotal = total;
              currentItem.DisplayTotal = total.toLocaleString();

              initArray.push(currentItem);
              this.producedGoods = [...this.producedGoods, ...initArray];
              this.selectedProduct = "";
              // end of manual yes
            }

            // start of manual No status
            if (product.HasUom === 1) {
              let ugp1Data = product.ugp1;
              const ugp1Record = ugp1Data.find(record => {
                return record.UomEntry === product.IUoMEntry;
              });
              currentItem.ugp1 = product.ugp1;
              currentItem.Price = ugp1Record.SellingPrice;
              currentItem.BuyingPrice = ugp1Record.BuyingPrice;
              currentItem.currentPrice = ugp1Record.SellingPrice;
              const total = 1 * ugp1Record.SellingPrice;
              currentItem.LineTotal = total;
              currentItem.DisplayTotal = total.toLocaleString();

              // push to array
              initArray.push(currentItem);
              this.producedGoods = [...this.producedGoods, ...initArray];
              this.selectedProduct = "";
              self.rawMaterialProducts = [];
              // end of push
            }
          } else {
            this.$refs.snackbar.show(res.msg, "red");
          }
        })
        .catch(err => {
          this.$refs.snackbar.show(err, "red");
        });
    },
    querySelections(searchText) {
      const self = this;
      const url = "/products/search";

      var customerId = 0;

      const data = {
        SearchText: searchText,
        CustomerId: customerId
      };

      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            self.products = res.ResponseData;
          } else {
            self.$store.commit("btnLoader", false);
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.$store.commit("btnLoader", false);
          this.$refs.snackbar.show(err, "red");
        });
    },
    queryRawMaterials(searchText) {
      const self = this;
      const url = "/products/search";

      var customerId = 0;

      const data = {
        SearchText: searchText,
        CustomerId: customerId
      };

      this.$store
        .dispatch("post", { url, data })
        .then(res => {
          if (res.ResultCode == 1200) {
            self.rawMaterialProducts = res.ResponseData;
          } else {
            self.$store.commit("btnLoader", false);
            this.$refs.snackbar.show(res.ResultDesc, "red");
          }
        })
        .catch(err => {
          self.$store.commit("btnLoader", false);
          this.$refs.snackbar.show(err, "red");
        });
    }
  }
};
</script>
